import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Table, Row, Col, Card, CardBody, Badge, Pagination, PaginationItem, PaginationLink, Label } from "reactstrap";
import { useDispatch } from "react-redux";
import { loadingTrue, loadingFalse } from "../../Reducer/loaderSlice";
import Handler from "../../Helpers/Handler";
import { toast } from "react-toastify";

// MUI DatePicker Imports
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const InvoiceTDSList = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [tdsList, setTdsList] = useState([]);
    const [summary, setSummary] = useState({ total_records: 0, total_tds: 0 });
    const [pagination, setPagination] = useState({ current_page: 1, total_pages: 1 });

    // API filters-ku match aagura state (Dates initialized as null for MUI)
    const [filters, setFilters] = useState({
        from_date: null,
        to_date: null,
        page: 1,
        limit: 10,
        sort_by: "id",
        sort_order: "DESC"
    });

    const loadTDS = async () => {
        dispatch(loadingTrue());
        try {
            const params = {
                ...filters,
                from_date: filters.from_date ? dayjs(filters.from_date).format('YYYY-MM-DD') : "",
                to_date: filters.to_date ? dayjs(filters.to_date).format('YYYY-MM-DD') : ""
            };
            const queryParams = new URLSearchParams(params).toString();
            const res = await Handler({ method: "get", url: `/tds/list.php?${queryParams}` });

            if (res?.success) {
                setTdsList(res?.data || []);
                setSummary(res?.summary);
                setPagination({
                    current_page: res.page,
                    total_pages: res.total_pages,
                    total_records: res.total
                });
            } else {
                toast.error(res?.message || "Failed to fetch TDS data");
            }
        } catch (err) {
            toast.error("Server connection error");
        } finally {
            dispatch(loadingFalse());
        }
    };

    useEffect(() => { loadTDS(); }, [filters.page]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div className="page-container p-1">
                <div className="page-header mb-4">
                    <h6 className="text-muted">Management &gt; TDS Records</h6>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="fw-bold">Invoice TDS Directory</h3>
                            <p className="text-muted">Track and manage all tax deductions applied to invoices.</p>
                        </div>
                        <Button className="add-btn" onClick={() => navigate("/invoices/tds/add")}>
                            <i className="bx bx-plus"></i> Apply New TDS
                        </Button>
                    </div>
                </div>

                {/* Summary Info Cards */}
                <Row className="mb-4 g-3">
                    <Col md={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-primary text-white text-center">
                            <CardBody className="p-3">
                                <small className="opacity-75 text-uppercase fw-bold">Total TDS Amount</small>
                                <h3 className="fw-bold mb-0">₹ {summary.total_tds?.toLocaleString()}</h3>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={6}>
                        <Card className="border-0 shadow-sm rounded-4 bg-dark text-white text-center">
                            <CardBody className="p-3">
                                <small className="opacity-75 text-uppercase fw-bold">Total TDS Entries</small>
                                <h3 className="fw-bold mb-0">{summary.total_records}</h3>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {/* Filter Section */}
                <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '15px' }}>
                    <CardBody className="p-3">
                        <Row className="g-3 align-items-end">
                            <Col md={4}>
                                <Label className="small fw-bold">From Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.from_date}
                                    onChange={(val) => setFilters({ ...filters, from_date: val, page: 1 })}
                                    maxDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } }
                                        }
                                    }}
                                />
                            </Col>
                            <Col md={4}>
                                <Label className="small fw-bold">To Date</Label>
                                <DatePicker
                                    disableFuture
                                    format="DD/MM/YYYY"
                                    value={filters.to_date}
                                    onChange={(val) => setFilters({ ...filters, to_date: val, page: 1 })}
                                    minDate={filters.from_date}
                                    maxDate={dayjs()}
                                    slotProps={{
                                        textField: {
                                            size: 'small',
                                            fullWidth: true,
                                            sx: { '& .MuiInputBase-root': { height: '45px', borderRadius: '8px' } }
                                        }
                                    }}
                                />
                            </Col>
                            <Col md={4}>
                                <Button color="primary" block style={{ height: '45px' }} onClick={loadTDS}>
                                    <i className="bx bx-filter-alt me-1"></i> Search History
                                </Button>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Table Section */}
                <Card className="border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                    <CardBody className="p-0">
                        <Table responsive hover className="mb-0 custom-table align-middle">
                            <thead className="bg-light">
                                <tr>
                                    <th className="ps-4">S.No</th>
                                    <th>Invoice Details</th>
                                    <th className="text-center">TDS Percentage</th>
                                    <th className="text-center">TDS Amount</th>
                                    <th>Reason / Note</th>
                                    <th>Applied Date</th>
                                    <th className="text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tdsList.length > 0 ? (
                                    tdsList.map((item, index) => (
                                        <tr key={item.id}>
                                            <td className="ps-4">{(filters.page - 1) * filters.limit + index + 1}</td>
                                            <td>
                                                <div className="fw-bold text-dark">{item.invoice.invoice_no}</div>
                                                <small className="text-muted">Bill Amt: ₹{item.invoice.total_amount?.toLocaleString()}</small>
                                            </td>
                                            <td className="text-center">
                                                <Badge color="info" pill>{item.tds.percentage}%</Badge>
                                            </td>
                                            <td className="text-center">
                                                <div className="fw-bold text-danger">₹ {item.tds.amount?.toLocaleString()}</div>
                                            </td>
                                            <td style={{ maxWidth: '200px' }}>
                                                <div className="text-truncate small">{item.note || <span className="text-muted italic">No note</span>}</div>
                                            </td>
                                            <td>{dayjs(item.created_at).format('DD-MM-YYYY')}</td>
                                            <td className="text-center">
                                                <Button color="link" className="p-0 text-info" onClick={() => navigate(`/tds/${item.id}`)}>
                                                    <i className="bx bx-edit fs-5"></i>
                                                </Button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-5">No TDS records found for the selected period.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </CardBody>
                </Card>

                {/* Pagination */}
                {pagination?.total_pages > 1 && (
                    <div className="d-flex justify-content-center mt-4">
                        <Pagination className="shadow-sm">
                            <PaginationItem disabled={filters.page <= 1}>
                                <PaginationLink previous onClick={() => setFilters({ ...filters, page: filters.page - 1 })} />
                            </PaginationItem>
                            {[...Array(pagination?.total_pages)].map((_, i) => (
                                <PaginationItem active={i + 1 === filters.page} key={i}>
                                    <PaginationLink onClick={() => setFilters({ ...filters, page: i + 1 })}>{i + 1}</PaginationLink>
                                </PaginationItem>
                            ))}
                            <PaginationItem disabled={filters.page >= pagination?.total_pages}>
                                <PaginationLink next onClick={() => setFilters({ ...filters, page: filters.page + 1 })} />
                            </PaginationItem>
                        </Pagination>
                    </div>
                )}
            </div>
        </LocalizationProvider>
    );
};

export default InvoiceTDSList;